import React from "react";
import { useTranslation } from "react-i18next";
import { formatLength } from "./fieldLimits";
import tailwindClassnames from "./TailwindClassnames";

type TextInputProps = {
  name: string;
  label: string;
  labelSuffix?: React.ReactNode;
  placeholder: string;
  value: string;
  error?: string;
  disabled: boolean;
  className: string;
  type: string;
  initialFocus?: boolean;
  limit?: number;
  // AKL-728: a soft limit shows a live "1,842 / 10,000" counter and sets NO maxLength, so
  // nothing is silently truncated. Use it on narrative fields, where the tail of a paste
  // disappearing unnoticed is how the original description bug got misattributed to the
  // citation field. `limit` stays the hard cap for short fields, where losing a character
  // is obvious. Passing both is a mistake; softLimit wins.
  softLimit?: number;
  rows?: number; // Add this prop for multiline input
  // Multiline only: let the user drag the box taller when they need more room.
  // Vertical only, so a textarea can never break out of its form grid column.
  resizable?: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

const TextInput: React.FC<TextInputProps> = ({
  name,
  label,
  labelSuffix,
  placeholder,
  value,
  error,
  disabled,
  className,
  type,
  initialFocus,
  limit,
  softLimit,
  rows,
  resizable = false,
  onChange,
  onKeyPress,
  onBlur,
}) => {
  const { i18n } = useTranslation();

  const hasSoftLimit = softLimit !== undefined;
  const hardLimit = hasSoftLimit ? undefined : limit;
  const length = value?.length ?? 0;
  const overSoftLimit = hasSoftLimit && length > softLimit;
  const counterId = `${name}-character-count`;
  const errorId = `${name}-error`;
  const describedBy =
    [error ? errorId : null, hasSoftLimit ? counterId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  // Prevent mouse wheel from changing number input values (AKL-500)
  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (type === "number") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  return (
    <div className="flex flex-col items-start">
      <label htmlFor={name} className={tailwindClassnames.textInputLabel}>
        <span className="flex items-center gap-1">
          {label}
          {labelSuffix}
        </span>
      </label>
      {rows ? (
        <textarea
          id={name}
          name={name}
          placeholder={placeholder}
          maxLength={hardLimit}
          value={value}
          disabled={disabled}
          className={`${className} ${resizable ? "resize-y" : "resize-none"}`}
          rows={rows}
          aria-describedby={describedBy}
          onChange={onChange}
        />
      ) : (
        <input
          id={name}
          maxLength={hardLimit}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          className={className}
          autoFocus={initialFocus}
          onChange={onChange}
          onKeyUp={onKeyPress}
          onBlur={onBlur}
          onWheel={handleWheel}
          aria-describedby={describedBy}
        />
      )}
      {hasSoftLimit && (
        <p
          id={counterId}
          // Announced only once over the limit. A live region on every keystroke would
          // make a screen reader read the count out continuously, which is noise; the
          // count itself is still reachable via aria-describedby on focus.
          aria-live={overSoftLimit ? "polite" : "off"}
          data-testid={`${name}-character-count`}
          className={`text-sm my-1 ${
            overSoftLimit
              ? "text-primary-orange-dark"
              : "text-primary-off-black/60 dark:text-primary-off-white/60"
          }`}
        >
          {`${formatLength(length, i18n.language)} / ${formatLength(
            softLimit,
            i18n.language,
          )}`}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2 my-1"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default TextInput;
